/**
 * GameInfoPanel — Reusable multiplayer game info sidebar
 *
 * Displays room code, player role, current turn, game status, and player count
 * in a compact panel positioned on the right side of the game board.
 * Customizable per game via props.
 */

import React from 'react';
import './GameInfoPanel.css';

export interface GameInfoItem {
  label: string;
  value: string;
  highlight?: boolean;  // Highlight this item (e.g., when it's your turn)
  icon?: string;        // Optional emoji icon
}

export interface GameInfoPanelProps {
  items: GameInfoItem[];
  turnIndicator?: {
    isMyTurn: boolean;
    mySymbol: string;
    currentPlayer: string;
  };
  position?: 'right' | 'top' | 'inline';  // Layout position
  compact?: boolean;
}

const GameInfoPanel: React.FC<GameInfoPanelProps> = ({
  items,
  turnIndicator,
  position = 'right',
  compact = false,
}) => {
  const panelClass = [
    'game-info-panel',
    `game-info-panel--${position}`,
    compact ? 'game-info-panel--compact' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={panelClass}>
      {/* Info items */}
      <div className="game-info-panel__items">
        {items.map((item, idx) => (
          <div
            key={idx}
            className={`game-info-panel__item ${item.highlight ? 'game-info-panel__item--highlight' : ''}`}
          >
            {item.icon && <span className="game-info-panel__icon">{item.icon}</span>}
            <span className="game-info-panel__label">{item.label}</span>
            <span className="game-info-panel__value">{item.value}</span>
          </div>
        ))}
      </div>

      {/* Turn indicator */}
      {turnIndicator && (
        <div className={`game-info-panel__turn ${turnIndicator.isMyTurn ? 'game-info-panel__turn--my-turn' : 'game-info-panel__turn--waiting'}`}>
          <span className="game-info-panel__turn-icon">
            {turnIndicator.isMyTurn ? '🎯' : '⏳'}
          </span>
          <span className="game-info-panel__turn-text">
            {turnIndicator.isMyTurn
              ? `Your turn (${turnIndicator.mySymbol})`
              : `Waiting for ${turnIndicator.currentPlayer}`
            }
          </span>
        </div>
      )}
    </div>
  );
};

export default GameInfoPanel;
